import { ApiPagination, ProductGrid, ScreenHeader } from "@/components";
import ScrollToTop from "@/components/ScrollToTop";
import { fetchCustomPaginatedCategoryWiseProductsQueryOptions } from "@/queryOptions/products/fetchCustomPaginatedCategoryWiseProducts";
import { useAppSelector } from "@/store/hooks";
import { ProductVariant } from "@/types/AppTypes";
import { getDefaultParseVariants } from "@/utils/helper/getDefaultParseVariants";
import { parseJSONField } from "@/utils/helper/parseJSON";
import { stringifyAttributes } from "@/utils/helper/stringifyAttributes";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const CategoryProducts = () => {
  const { id: categoryId } = useParams<{ id: string }>();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Get category info from store (assuming you have category in store)
  const { categories } = useAppSelector((state) => state.app); // Adjust according to your store structure

  const {
    data: queryResult,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isSuccess,
  } = useInfiniteQuery(
    fetchCustomPaginatedCategoryWiseProductsQueryOptions(
      {},
      9,
      categoryId || "",
      {
        enabled: !!categoryId,
        staleTime: 1000 * 60 * 5,
      }
    )
  );

  // Get current page data
  const currentPage = queryResult?.pages?.[currentPageIndex];

  // Process products for display
  const categoryProductsForDisplay = useMemo(() => {
    if (!categoryId || !currentPage?.items) return [];
    const processedProducts = getDefaultParseVariants(currentPage.items);

    return processedProducts?.sort((a: ProductVariant, b: ProductVariant) => {
      const aAttr = stringifyAttributes(parseJSONField(a?.attributes));
      const bAttr = stringifyAttributes(parseJSONField(b?.attributes));
      return aAttr.localeCompare(bAttr);
    });
  }, [currentPage?.items, categoryId]);

  // Get category name for display
  const formattedBrand = useMemo(() => {
    if (!categoryId || !categories) return "Category Products";
    const category = categories.find((b: any) => b.id === categoryId);
    return category?.name || "Category Products";
  }, [categoryId, categories]);

  useEffect(() => {
    if (isSuccess && queryResult?.pages?.length === 1 && hasNextPage) {
      fetchNextPage();
    }
  }, [isSuccess, queryResult?.pages?.length, hasNextPage, fetchNextPage]);

  const totalPages = queryResult?.pages?.length || 0;

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = async () => {
    const nextPageIndex = currentPageIndex + 1;

    if (queryResult?.pages?.[nextPageIndex]) {
      setCurrentPageIndex(nextPageIndex);

      if (!queryResult.pages[nextPageIndex + 1] && hasNextPage) {
        fetchNextPage();
      }
    } else if (hasNextPage) {
      // If next page doesn't exist but more data is available, fetch it
      await fetchNextPage();
      setCurrentPageIndex(nextPageIndex);
    }
  };

  if (!categoryId) {
    return <div className="px-5 py-8">Brand not specified</div>;
  }

  return (
    <div className="px-5">
      <ScrollToTop dependencies={[currentPageIndex]} />
      <ProductGrid
        loading={isLoading && currentPageIndex === 0} // Only show loading for first page
        skeletonCount={6}
        className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4"
        gridItems={categoryProductsForDisplay}
        sectionHeader={
          <ScreenHeader
            title={formattedBrand}
            description={`Showing ${
              categoryProductsForDisplay?.length || 0
            } products`}
          />
        }
      />

      {/* Show pagination when not loading initial data */}
      {!isLoading && (
        <div className="flex justify-end items-center my-10 container mx-auto px-0 max-w-6xl pb-10 pt-2">
          <ApiPagination
            currentPageIndex={currentPageIndex}
            totalPages={totalPages}
            hasNextPage={currentPageIndex < (queryResult?.pages?.length || 0) - 1 || hasNextPage}
            isFetching={isFetchingNextPage}
            onPrevious={handlePrevious}
            onNext={handleNext}
            variant="minimal"
          />
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;
